//@author travis
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Input,
    OnChanges,
    OnInit,
    SimpleChanges,
    ViewChild,
    ViewEncapsulation,
    inject,
} from "@angular/core";
import { S25ImageService } from "../../s25-image/s25.image.service";
import { S25EditableAbstract } from "../s25.editable.abstract";
import { ModalData, ModalService } from "../../modal/modal.service";
import { ImageModelI, S25ImageComponent } from "../../s25-image/s25.image.component";
import { S25Util } from "../../../util/s25-util";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { ImageService } from "../../../services/image.service";
import { ModalImageI } from "../../modal/modals/modal.image.upload.component";
import { ImageActionDataI } from "../../s25-image-upload/s25.image.upload.component";
import { FlsService } from "../../../services/fls.service";

@TypeManagerDecorator("s25-ng-editable-image")
@Component({
    selector: "s25-ng-editable-image",
    template: ` @if (init) {
        <div class="editable-image" [ngClass]="{ 'cust-attr-img': custAttrType }">
            @if (!readOnly()) {
                <button class="btn btn-link c-evdd-image-upload" [id]="fieldId()" (click)="openModal()">
                    {{ custAttrType ? "Add" : "Edit" }}
                </button>
            }
            @if (!displayImage && (val || model.imageUrl)) {
                <s25-popover [popoverTemplate]="imagePreview" [openTrigger]="'click'" [closeTrigger]="'click'">
                    <button class="aw-button aw-button--outline">Preview</button>
                </s25-popover>
            }
            <ng-template #imagePreview>
                <s25-ng-image [model]="model"></s25-ng-image>
            </ng-template>
            @if (displayImage && (val || model?.imageUrl)) {
                <ng-container [ngTemplateOutlet]="imagePreview"></ng-container>
            }
        </div>
    }`,
    styles: `
        .btn.btn-link.c-evdd-image-upload {
            font-size: 1rem;
            padding: unset;
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
// number | string | undefined
// imageId (number) or full URL string (e.g. from ingestion form or Trumba cust atrb id (-20, -21))
export class S25EditableImageComponent
    extends S25EditableAbstract<number | string | undefined>
    implements OnInit, OnChanges
{
    @Input() showImage: boolean = true;
    @Input() width: number;
    @Input() autoSaveUpload: boolean;
    @Input() onCommit?: (inputVal: any) => any; //defined in editable abstract
    @Input() imageTypeName?: "Layout Diagram" | "Layout Image" | "Image" | "Diagram";
    @Input() custAttrType?: "EventImage" | "DetailImage";
    @Input() url?: string;

    model: ImageModelI;
    modalModel: ModalImageI & Partial<ModalData>;
    init = false;
    displayImage = true;
    hasUploadPerms: boolean;

    getType = () => "image";

    @ViewChild(S25ImageComponent) imageChild: S25ImageComponent;

    private readonly elementRef = inject(ElementRef);
    private readonly cd = inject(ChangeDetectorRef);

    ngOnChanges(changes: SimpleChanges) {
        if (changes["val"] || changes["url"]) {
            this.init = false;
            this.cd.markForCheck();
            this.ngOnInit();
        }
    }

    private static isUrlString(v: unknown): v is string {
        return typeof v === "string" && (v.startsWith("http://") || v.startsWith("https://"));
    }

    private static toImageId(v: number | string | undefined): number | undefined {
        if (v == null) return undefined;
        if (typeof v === "number") return v;
        const n = parseInt(String(v).trim(), 10);
        return !isNaN(n) && !!n ? n : undefined;
    }

    setImage = (imageId: number | undefined, url: string | undefined) => {
        this.model = {
            imageId: imageId ?? undefined,
            imageUrl: url || (imageId ? S25ImageService.getUrl(imageId, this.width) : undefined),
        };
    };

    ngOnInit() {
        super.ngOnInit();

        FlsService.getFls().then((data) => {
            this.hasUploadPerms = data.SPACE_VIEW === "F";

            this.imageTypeName = S25Util.contains(this.imageTypeName?.toLowerCase(), "diagram") ? "Diagram" : "Image";
            // Support imageId (number), numeric string, or full URL (from ingestion form or Trumba cust atrb id (-20, -21)).
            // If URL matches our image URL pattern, extract imageId and use that for consistency.
            const directImageId = S25EditableImageComponent.toImageId(this.val);
            const urlString = this.url || (S25EditableImageComponent.isUrlString(this.val) ? this.val : undefined);
            const imageIdFromUrl = urlString ? S25ImageService.parseImageIdFromUrl(urlString) : undefined;
            const imageId = directImageId ?? imageIdFromUrl;
            const imageUrl = imageId ? undefined : urlString; // use URL only when we can't derive imageId
            this.setImage(imageId, imageUrl);
            this.displayImage = this.showImage && (!!this.val || !!this.url);
            const processChosenImage = (imageId: number) => {
                if (imageId) {
                    this.onCommit && this.onCommit({ imageId: imageId, imageType: this.imageTypeName });
                    setImageData(imageId);
                    // modify image url for trumba recognition
                    if (this.custAttrType) {
                        return ImageService.getImage(imageId).then((resp) => {
                            const input =
                                this.elementRef.nativeElement.parentElement.parentElement.parentElement.querySelector(
                                    `.${this.custAttrType}-input`,
                                );

                            const imageUrl = S25ImageService.getUrl(imageId, this.width);
                            if (imageUrl) {
                                const prevImageUrl = imageUrl.split("&");

                                if (prevImageUrl.length > 0) {
                                    prevImageUrl[1] = `filename=${resp.file_name}`;
                                    const trumbaUrl = prevImageUrl.slice(0, 2).join("&");

                                    window.angBridge.$rootScope.$broadcast(this.custAttrType, trumbaUrl);

                                    if (input) input.value = trumbaUrl;
                                    this.model.imageUrl = trumbaUrl;
                                }
                            }
                        });
                    }
                }
            };

            const setImageData = (imageId: number) => {
                this.modalModel.imageUrl = null;
                this.model.imageId = imageId;
                this.model.imageUrl = null;
                this.model.data = imageId;
                this.val = imageId;
                this.displayImage = this.showImage;
                this.cd.detectChanges();
                this.imageChild && this.imageChild.init(this.model);
                this.modalModel.closeModal();
            };

            const validateName = (modalModel: any) => {
                modalModel.errorMsg = "";
                if (!modalModel.imageModel.imageName) {
                    modalModel.errorMsg = "Please enter a name for the image";
                } else if (modalModel.imageModel.imageName.length > 40) {
                    modalModel.errorMsg = "Image name cannot be longer than 40 characters";
                }
                return !modalModel.errorMsg;
            };

            this.modalModel = {
                onDone: () => {
                    this.modalModel.errorMsg = null;
                    if (this.modalModel.isUpload && this.hasUploadPerms) {
                        if (validateName(this.modalModel)) {
                            this.modalModel.imageModel.getImageData().then((imageData: string) => {
                                if (imageData) {
                                    const actionData: ImageActionDataI = {
                                        imageName: this.modalModel.imageModel.imageName,
                                        imageDesc: this.modalModel.imageModel.imageDesc,
                                        imageTypeId: this.modalModel.imageModel.imageTypeId, // Categorizing as Photograph/Diagram
                                        imageFileName: this.modalModel.imageModel.getImageFileName(),
                                        imageData: S25Util.extractBase64FromURI(imageData),
                                        imageType: this.modalModel.imageModel.getImageType(),
                                    };
                                    if (S25Util.toBool(this.autoSaveUpload)) {
                                        ImageService.postImage(
                                            actionData.imageName,
                                            actionData.imageDesc,
                                            actionData.imageFileName,
                                            actionData.imageData,
                                            actionData.imageTypeId,
                                        ).then((resp) => {
                                            processChosenImage(resp);
                                        });
                                    } else {
                                        this.onCommit(actionData).then(() => {
                                            if (actionData.imageId) {
                                                setImageData(actionData.imageId);
                                            } else {
                                                this.modalModel.imageUrl = null;
                                                this.model.imageId = null;
                                                this.model.imageUrl = null;
                                                this.model.data = actionData.imageData;
                                                this.model.fileType = actionData.imageType;
                                                this.displayImage = true;
                                                this.cd.detectChanges();
                                                this.imageChild && this.imageChild.init(this.model);
                                                this.modalModel.closeModal();
                                            }
                                        });
                                    }
                                } else {
                                    this.modalModel.errorMsg = "Please upload an image";
                                }
                            });
                        }
                    } else {
                        // select mode
                        if (this.modalModel.selectModel.selectedImage) {
                            const imageId = parseInt(this.modalModel.selectModel.selectedImage.image_id);
                            processChosenImage(imageId);
                        } else {
                            this.modalModel.errorMsg = "Please select an image";
                        }
                    }
                },

                imageTypeName: this.imageTypeName,
                hasUploadPerms: this.hasUploadPerms,
            };

            this.init = true;
            this.cd.detectChanges();
        });
    }

    openModal() {
        ModalService.modal("image-upload", this.modalModel);
    }
}
